import { Link } from "react-router-dom";
import { ArrowUpRight, Mountains, MapPin, Path, Heart } from "@phosphor-icons/react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";

const difficultyColor = {
  Easy: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Moderate: "bg-amber-50 text-amber-800 border-amber-200",
  Hard: "bg-orange-50 text-orange-800 border-orange-200",
  Expert: "bg-red-50 text-red-800 border-red-200",
};

export default function RouteCard({ route, size = "default" }) {
  const isLarge = size === "large";
  const { user, favorites, toggleFavorite } = useAuth();
  const isFav = favorites.includes(route.id);

  const handleFav = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      toast.error("Sign in to save favourites");
      return;
    }
    await toggleFavorite(route.id);
    toast.success(isFav ? "Removed from favourites" : "Saved to favourites");
  };

  return (
    <Link
      to={`/routes/${route.id}`}
      data-testid={`route-card-${route.id}`}
      className={`group block bg-white border border-[#E5E0D8] rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(44,85,69,0.12)] transition-all duration-300 ${
        isLarge ? "lg:col-span-2 lg:row-span-2" : ""
      }`}
    >
      <div className={`relative overflow-hidden ${isLarge ? "h-80 lg:h-[420px]" : "h-56"}`}>
        <img
          src={route.hero_image}
          alt={route.name}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />

        <button
          onClick={handleFav}
          data-testid={`favorite-button-${route.id}`}
          className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center backdrop-blur-sm transition-all ${
            isFav ? "bg-[#C86B4E] text-white" : "bg-black/40 text-white hover:bg-black/60"
          }`}
          aria-label={isFav ? "Remove favourite" : "Add favourite"}
        >
          <Heart size={18} weight={isFav ? "fill" : "regular"} />
        </button>

        <div className="absolute top-4 left-4 flex items-center gap-2">
          <span className={`text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full border ${difficultyColor[route.difficulty] || "bg-white/90 text-[#1A1D1A] border-white"}`}>
            {route.difficulty}
          </span>
          <span className="text-xs uppercase tracking-wider font-bold px-3 py-1 rounded-full bg-black/40 text-white backdrop-blur-sm">
            {route.days} days
          </span>
        </div>
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between text-white">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-90 flex items-center gap-1">
              <MapPin size={12} weight="fill" /> {route.location}, {route.country}
            </div>
            <h3 className={`font-display font-black mt-1 leading-tight ${isLarge ? "text-3xl lg:text-4xl" : "text-xl"}`}>
              {route.name}
            </h3>
          </div>
          <ArrowUpRight size={isLarge ? 36 : 24} className="opacity-0 group-hover:opacity-100 -translate-x-2 group-hover:translate-x-0 transition-all" />
        </div>
      </div>

      <div className="p-5 grid grid-cols-2 gap-4">
        <div className="flex items-center gap-2">
          <Path size={18} className="text-[#2C5545]" weight="duotone" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C665F] font-bold">Distance</div>
            <div className="text-sm font-bold text-[#1A1D1A]">{route.distance_km} km</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Mountains size={18} className="text-[#2C5545]" weight="duotone" />
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#5C665F] font-bold">Elevation</div>
            <div className="text-sm font-bold text-[#1A1D1A]">{route.elevation_m.toLocaleString()} m</div>
          </div>
        </div>
      </div>
    </Link>
  );
}
